import { useMemo } from "react";
import Select from "react-select";
import {
  DEFAULT_FX_TRADE_TYPE,
  FX_ASSET_TYPE,
  FX_TRADE_TYPE_OPTIONS,
  getAssetTypeOptions,
  getCategoryOptionsForAsset,
  getCountryOptionsForAsset,
  getTickerOptionsForSelection,
} from "./TickerUniverseOptions";
import {
  autoScrollSelectComponents,
} from "../Common/SelectMenuScroll";

const selectPortalStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? "rgba(13, 110, 253, 0.35)" : "var(--app-border-strong, #D8DEE6)",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(13, 110, 253, 0.08)"
      : "none",
    "&:hover": {
      borderColor: state.isFocused ? "rgba(13, 110, 253, 0.35)" : "var(--app-border-hover, #C8D0DA)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: 10,
    paddingRight: 10,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "var(--app-border, #E5EAF1)",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export const buildCombinedSelectStyles = ({
  compact = false,
  accent = false,
} = {}) => ({
  ...selectPortalStyles,
  control: (base, state) => ({
    ...selectPortalStyles.control(base, state),
    minHeight: compact ? 38 : 42,
    borderRadius: compact ? 13 : 15,
    background: "var(--app-panel-bg, #FFFFFF)",
    borderColor: state.isFocused
      ? "rgba(13, 110, 253, 0.40)"
      : accent
        ? "rgba(13, 110, 253, 0.26)"
        : "var(--app-border-strong, #D7E0EA)",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(13, 110, 253, 0.10)"
      : "0 1px 2px rgba(15, 23, 42, 0.04)",
    "&:hover": {
      borderColor: state.isFocused
        ? "rgba(13, 110, 253, 0.40)"
        : accent
          ? "rgba(13, 110, 253, 0.3)"
          : "var(--app-border-hover, #C8D4E0)",
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--app-text, #1E293B)",
    fontSize: compact ? "0.84rem" : "0.89rem",
    fontWeight: accent ? 650 : 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#7A8797",
    fontSize: compact ? "0.78rem" : "0.82rem",
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#0D6EFD" : "#7A8797",
    padding: compact ? "0 6px 0 2px" : "0 8px 0 4px",
  }),
  valueContainer: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: compact ? 7 : 9,
    paddingRight: compact ? 7 : 9,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    minHeight: compact ? 38 : 42,
  }),
  input: (base) => ({
    ...base,
    color: "var(--app-text, #1E293B)",
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
  }),
});

const FilterField = ({
  children,
  className = "",
  label,
}) => (
  <div className={`cotrader-combined-filter__field ${className}`.trim()}>
    <div className="cotrader-combined-filter__label">{label}</div>
    {children}
  </div>
);

const CoTraderCombinedFilter = ({
  universeOptions = {},
  selection,
  onSelectionChange,
  isDisabled = false,
  tradeTypeOptions = FX_TRADE_TYPE_OPTIONS,
  chartInterval = "1m",
  chartPeriod = "5d",
  chartIntervalOptions = [],
  chartPeriodOptions = [],
  onChartIntervalChange = null,
  onChartPeriodChange = null,
  showChartControls = true,
  topRightContent = null,
}) => {
  const assetTypeOptions = useMemo(
    () => getAssetTypeOptions(universeOptions),
    [universeOptions],
  );
  const countryOptions = useMemo(
    () => getCountryOptionsForAsset(universeOptions, selection.assetType),
    [selection.assetType, universeOptions],
  );
  const categoryOptions = useMemo(
    () =>
      getCategoryOptionsForAsset(
        universeOptions,
        selection.assetType,
        selection.country,
      ),
    [selection.assetType, selection.country, universeOptions],
  );
  const tickerOptions = useMemo(
    () =>
      getTickerOptionsForSelection(
        universeOptions,
        selection.assetType,
        selection.country,
        selection.category,
      ),
    [selection.assetType, selection.category, selection.country, universeOptions],
  );
  const selectedTickerOption = useMemo(
    () =>
      tickerOptions.find((option) => option.value === selection.value) || null,
    [selection.value, tickerOptions],
  );
  const selectedAssetTypeOption = useMemo(
    () =>
      assetTypeOptions.find((option) => option.value === selection.assetType) ||
      null,
    [assetTypeOptions, selection.assetType],
  );
  const selectedCountryOption = useMemo(
    () =>
      countryOptions.find((option) => option.value === selection.country) || null,
    [countryOptions, selection.country],
  );
  const selectedCategoryOption = useMemo(
    () =>
      categoryOptions.find((option) => option.value === selection.category) || null,
    [categoryOptions, selection.category],
  );
  const selectedTradeTypeOption = useMemo(
    () =>
      tradeTypeOptions.find((option) => option.value === selection.tradeType) ||
      tradeTypeOptions[0] ||
      null,
    [selection.tradeType, tradeTypeOptions],
  );
  const isFxAsset = selection.assetType === FX_ASSET_TYPE;
  const selectedChartIntervalOption = useMemo(
    () =>
      chartIntervalOptions.find((option) => option.value === chartInterval) || null,
    [chartInterval, chartIntervalOptions],
  );
  const selectedChartPeriodOption = useMemo(
    () =>
      chartPeriodOptions.find((option) => option.value === chartPeriod) || null,
    [chartPeriod, chartPeriodOptions],
  );
  const defaultSelectStyles = useMemo(() => buildCombinedSelectStyles(), []);
  const primarySelectStyles = useMemo(
    () => buildCombinedSelectStyles({ accent: true }),
    [],
  );
  const chartSelectStyles = useMemo(
    () => buildCombinedSelectStyles({ compact: true }),
    [],
  );
  const layoutClassName = showChartControls
    ? isFxAsset
      ? "cotrader-combined-filter__grid cotrader-combined-filter__grid--fx"
      : "cotrader-combined-filter__grid cotrader-combined-filter__grid--scoped"
    : isFxAsset
      ? "cotrader-combined-filter__grid cotrader-combined-filter__grid--fx-no-chart"
      : "cotrader-combined-filter__grid cotrader-combined-filter__grid--scoped-no-chart";

  return (
    <div className="w-100 cotrader-combined-filter">
      <style>
        {`
          .cotrader-combined-filter__grid {
            display: grid;
            gap: 10px;
            align-items: end;
            grid-template-columns: repeat(18, minmax(0, 1fr));
          }
          .cotrader-combined-filter__grid--fx .cotrader-combined-filter__field--asset,
          .cotrader-combined-filter__grid--scoped .cotrader-combined-filter__field--asset,
          .cotrader-combined-filter__grid--fx-no-chart .cotrader-combined-filter__field--asset,
          .cotrader-combined-filter__grid--scoped-no-chart .cotrader-combined-filter__field--asset {
            grid-column: span 3;
          }
          .cotrader-combined-filter__grid--fx .cotrader-combined-filter__field--market,
          .cotrader-combined-filter__grid--scoped .cotrader-combined-filter__field--market,
          .cotrader-combined-filter__grid--fx-no-chart .cotrader-combined-filter__field--market,
          .cotrader-combined-filter__grid--scoped-no-chart .cotrader-combined-filter__field--market {
            grid-column: span 3;
          }
          .cotrader-combined-filter__grid--scoped .cotrader-combined-filter__field--universe,
          .cotrader-combined-filter__grid--scoped-no-chart .cotrader-combined-filter__field--universe {
            grid-column: span 3;
          }
          .cotrader-combined-filter__grid--scoped .cotrader-combined-filter__field--primary {
            grid-column: span 5;
          }
          .cotrader-combined-filter__grid--fx .cotrader-combined-filter__field--primary {
            grid-column: span 8;
          }
          .cotrader-combined-filter__grid--scoped-no-chart .cotrader-combined-filter__field--primary,
          .cotrader-combined-filter__grid--fx-no-chart .cotrader-combined-filter__field--primary {
            grid-column: span 12;
          }
          .cotrader-combined-filter__grid--fx .cotrader-combined-filter__field--chart,
          .cotrader-combined-filter__grid--scoped .cotrader-combined-filter__field--chart {
            grid-column: span 2;
          }
          .cotrader-combined-filter__field {
            min-width: 0;
          }
          .cotrader-combined-filter__field--primary {
            position: relative;
          }
          .cotrader-combined-filter__label {
            margin-bottom: 0.3rem;
            padding-left: 0.12rem;
            font-size: 0.62rem;
            font-weight: 700;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: #7A8797;
          }
          .cotrader-combined-filter__field--primary .cotrader-combined-filter__label {
            color: #5B6472;
          }
          .cotrader-combined-filter__field--chart .cotrader-combined-filter__label {
            color: #8A96A5;
          }
          @media (max-width: 767px) {
            .cotrader-combined-filter__grid,
            .cotrader-combined-filter__grid--fx,
            .cotrader-combined-filter__grid--fx-no-chart,
            .cotrader-combined-filter__grid--scoped,
            .cotrader-combined-filter__grid--scoped-no-chart {
              grid-template-columns: minmax(0, 1fr);
            }
            .cotrader-combined-filter__field--primary {
              grid-column: auto;
            }
          }
        `}
      </style>

      <div className="d-flex align-items-end gap-3 flex-nowrap" style={{ minWidth: 0 }}>
        <div className={layoutClassName} style={{ flex: "1 1 auto", minWidth: 0 }}>
        <FilterField
          label="Asset Class"
          className="cotrader-combined-filter__field--asset"
        >
          <Select
            options={assetTypeOptions}
            value={selectedAssetTypeOption}
            onChange={(nextAssetType) =>
              onSelectionChange({
                assetType: nextAssetType?.value || "",
                country: null,
                category: null,
                tradeType: DEFAULT_FX_TRADE_TYPE,
                value: "",
              })
            }
            isDisabled={isDisabled || assetTypeOptions.length === 0}
            menuPortalTarget={document.body}
            styles={defaultSelectStyles}
            placeholder="Asset Type"
            components={autoScrollSelectComponents}
          />
        </FilterField>

        {!isFxAsset ? (
          <FilterField
            label="Market"
            className="cotrader-combined-filter__field--market"
          >
            <Select
              options={countryOptions}
              value={selectedCountryOption}
              onChange={(nextCountry) =>
                onSelectionChange({
                  ...selection,
                  country: nextCountry?.value || null,
                  category: null,
                  value: "",
                })
              }
              isDisabled={
                isDisabled ||
                !selection.assetType ||
                countryOptions.length === 0
              }
              menuPortalTarget={document.body}
              styles={defaultSelectStyles}
              placeholder="Country"
              components={autoScrollSelectComponents}
            />
          </FilterField>
        ) : (
          <FilterField
            label="Mode"
            className="cotrader-combined-filter__field--market"
          >
            <Select
              options={tradeTypeOptions}
              value={selectedTradeTypeOption}
              onChange={(nextTradeType) =>
                onSelectionChange({
                  ...selection,
                  tradeType: nextTradeType?.value || DEFAULT_FX_TRADE_TYPE,
                })
              }
              isDisabled={isDisabled || tradeTypeOptions.length <= 1}
              menuPortalTarget={document.body}
              styles={defaultSelectStyles}
              placeholder="Spot / Forward"
              components={autoScrollSelectComponents}
            />
          </FilterField>
        )}

        {!isFxAsset ? (
          <FilterField
            label="Universe"
            className="cotrader-combined-filter__field--universe"
          >
            <Select
              options={categoryOptions}
              value={selectedCategoryOption}
              onChange={(nextCategory) =>
                onSelectionChange({
                  ...selection,
                  category: nextCategory?.value || null,
                  value: "",
                })
              }
              isDisabled={
                isDisabled ||
                !selection.assetType ||
                !selection.country ||
                categoryOptions.length === 0
              }
              menuPortalTarget={document.body}
              styles={defaultSelectStyles}
              placeholder="Instrument / Category"
              components={autoScrollSelectComponents}
            />
          </FilterField>
        ) : null}

        <FilterField
          label={isFxAsset ? "Currency Pair" : "Instrument"}
          className="cotrader-combined-filter__field--primary"
        >
          <Select
            options={tickerOptions}
            value={selectedTickerOption}
            onChange={(nextTicker) =>
              onSelectionChange({
                ...selection,
                value: nextTicker?.value || "",
              })
            }
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            isDisabled={isDisabled || tickerOptions.length === 0}
            menuPortalTarget={document.body}
            styles={primarySelectStyles}
            placeholder="Ticker"
            components={autoScrollSelectComponents}
          />
        </FilterField>

        {showChartControls ? (
          <>
            <FilterField
              label="Interval"
              className="cotrader-combined-filter__field--chart"
            >
              <Select
                options={chartIntervalOptions}
                value={selectedChartIntervalOption}
                onChange={(nextInterval) =>
                  onChartIntervalChange?.(nextInterval?.value || "1m")
                }
                isDisabled={isDisabled || chartIntervalOptions.length === 0}
                menuPortalTarget={document.body}
                styles={chartSelectStyles}
                placeholder="Interval"
                components={autoScrollSelectComponents}
              />
            </FilterField>

            <FilterField
              label="Period"
              className="cotrader-combined-filter__field--chart"
            >
              <Select
                options={chartPeriodOptions}
                value={selectedChartPeriodOption}
                onChange={(nextPeriod) =>
                  onChartPeriodChange?.(nextPeriod?.value || "5d")
                }
                isDisabled={isDisabled || chartPeriodOptions.length === 0}
                menuPortalTarget={document.body}
                styles={chartSelectStyles}
                placeholder="Period"
                components={autoScrollSelectComponents}
              />
            </FilterField>
          </>
        ) : null}
        </div>
        {topRightContent ? (
          <div className="d-flex align-items-center flex-shrink-0">
            {topRightContent}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CoTraderCombinedFilter;
